/**
 * @file React hook — useA2EnergyCalculator (state and side effects).
 */


import { useMemo } from "react";
import { FormDataResponse } from "../../../../../../interfaces/formInterfaces";

/**
 * Sums embodied energy (MJ) across A2 fuel rows.
 */
export function useA2EnergyCalculator({
  formData,
  values,
  sectionRows,
  /** Background finished material declared quantity (same basis as A1). */
  backgroundFinishedQty,
}: {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  sectionRows: Record<string, number>;
  backgroundFinishedQty?: string;
}) {
  const energySummaryMj = useMemo(() => {
    if (!formData) return 0;

    const fuelCode = "A2_FUEL_TRANSPORT";
    const fuelRows = sectionRows[fuelCode] || 0;
    const legacyEnergySec = formData.formSubSections?.find(
      (s) => s.code === "A2_EMBODIED_ENERGY"
    );
    const legacyRows = legacyEnergySec
      ? sectionRows[legacyEnergySec.code!] || 0
      : 0;

    const addRowContribution = (
      code: string,
      i: number,
      acc: { total: number }
    ) => {
      const val = Number(values[`embodiedEnergy_${code}_row${i}`] || 0);
      const unit = values[`embodiedEnergy_unit_${code}_row${i}`];
      if (!Number.isFinite(val) || val <= 0) return;
      if (unit === "kWh") acc.total += val * 3.6;
      else acc.total += val;
    };

    const acc = { total: 0 };

    for (let i = 0; i < fuelRows; i++) {
      addRowContribution(fuelCode, i, acc);
    }

    if (legacyRows > 0 && legacyEnergySec?.code) {
      const ec = legacyEnergySec.code;
      for (let i = 0; i < legacyRows; i++) {
        addRowContribution(ec, i, acc);
      }
    }

    return Math.round(acc.total * 1000) / 1000;
  }, [formData, values, sectionRows]);

  const energyPerDeclaredFinishedMaterialMj = useMemo(() => {
    const q = parseFloat(String(backgroundFinishedQty ?? "").trim());
    if (!Number.isFinite(q) || q <= 0) return null;
    return Math.round((energySummaryMj / q) * 1000) / 1000;
  }, [energySummaryMj, backgroundFinishedQty]);

  return { energySummaryMj, energyPerDeclaredFinishedMaterialMj };
}
